import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SearchableSelect } from '@/components/common';
import { useSearchRTOs } from '@/hooks/useGeography';
import type { MotorQuoteFormData } from '../../schema';

export function RTOStep() {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<MotorQuoteFormData>();

  const rtoCode = watch('rto_code');
  const rtoLabel = watch('rto_label');

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: rtoData, isFetching } = useSearchRTOs(debouncedQuery);
  const results = useMemo(() => rtoData?.results ?? [], [rtoData]);

  const options = useMemo(() => {
    const base = results.map((r) => ({ value: r.code, label: `${r.code} — ${r.name}, ${r.state}` }));
    // Keep the already-selected RTO visible even after the query changes and
    // the server-filtered result set no longer contains it.
    if (rtoCode && !base.some((o) => o.value === rtoCode)) {
      return [{ value: rtoCode, label: rtoLabel || rtoCode }, ...base];
    }
    return base;
  }, [results, rtoCode, rtoLabel]);

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">RTO</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Search for the Regional Transport Office the vehicle is registered under.
      </p>
      <div className="sm:max-w-sm">
        <SearchableSelect
          label="RTO"
          value={rtoCode || ''}
          onChange={(value) => {
            const picked = options.find((o) => o.value === value);
            setValue('rto_code', value, { shouldValidate: true, shouldDirty: true });
            setValue('rto_label', picked?.label ?? value, { shouldValidate: false });
          }}
          onQueryChange={setQuery}
          options={options}
          placeholder={isFetching ? 'Searching...' : 'Type an RTO code or city (min 2 characters)'}
          error={errors.rto_code?.message}
          required
        />
      </div>
    </div>
  );
}
